import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router";

import { SortingDirection } from "enums";
import { type GenericFiltersMap, type RangeFilterValues } from "utils/filterUtils";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_SEARCH_PARAM = "search";
const DEFAULT_PAGE_PARAM = "page";
const DEFAULT_PAGE_SIZE_PARAM = "pageSize";
const DEFAULT_SORT_PARAM = "sortBy";
const DEFAULT_SORT_DIRECTION_PARAM = "sortDir";

const sortingDirections = new Set([SortingDirection.Ascending, SortingDirection.Descending]);

const parsePositiveInteger = (value: string | null, fallback: number): number => {
  if (!value) {
    return fallback;
  }

  const parsed = Number.parseInt(value, 10);

  if (Number.isFinite(parsed) && parsed > 0) {
    return parsed;
  }

  return fallback;
};

const createRangeValue = (from?: string | null, to?: string | null): RangeFilterValues | undefined => {
  if (!from && !to) {
    return undefined;
  }

  return {
    from: from ?? "",
    to: to ?? "",
  };
};

const setParam = (params: URLSearchParams, key: string, value: string | null | undefined) => {
  if (value === undefined || value === null || value === "") {
    params.delete(key);
    return;
  }

  params.set(key, value);
};

const setArrayParam = (params: URLSearchParams, key: string, values: string[] | undefined) => {
  params.delete(key);

  if (!values || values.length === 0) {
    return;
  }

  values.filter(Boolean).forEach((value) => params.append(key, value));
};

type ValueFilterParser<V> = (value: string | null) => V | undefined;
type ValueFilterSerializer<V> = (value: V | undefined) => string | undefined;
type ArrayFilterParser<V> = (value: string) => V | undefined;
type ArrayFilterSerializer<V> = (value: V) => string | undefined;

interface BaseFilterDefinition<F extends string> {
  key: F;
}

export interface ArrayFilterDefinition<F extends string, V = string> extends BaseFilterDefinition<F> {
  type: "array";
  param?: string;
  parse?: ArrayFilterParser<V>;
  serialize?: ArrayFilterSerializer<V>;
}

export interface RangeFilterDefinition<F extends string> extends BaseFilterDefinition<F> {
  type: "range";
  fromParam: string;
  toParam: string;
}

export interface ValueFilterDefinition<F extends string, V = string> extends BaseFilterDefinition<F> {
  type: "value";
  param?: string;
  parse?: ValueFilterParser<V>;
  serialize?: ValueFilterSerializer<V>;
}

export type FilterDefinition<F extends string> =
  | ArrayFilterDefinition<F, unknown>
  | RangeFilterDefinition<F>
  | ValueFilterDefinition<F, unknown>;

interface SortingConfig {
  valid: readonly string[];
  defaultSortBy?: string;
  defaultDirection?: SortingDirection;
  sortKeyToColumnName?: Record<string, string>;
  fallbackColumnName?: string;
}

export interface TableQueryParamsConfig<F extends string> {
  defaultPageSize?: number;
  filters?: ReadonlyArray<FilterDefinition<F>>;
  sorting?: SortingConfig;
}

export interface TableQueryState<F extends string = string> {
  filters: GenericFiltersMap<F>;
  search: string;
  page: number;
  pageSize: number;
  sortBy?: string;
  sortDirection?: SortingDirection;
}

export interface TableQueryHandlers<F extends string = string> {
  setSearch: (value: string) => void;
  setFilters: (nextFilters: GenericFiltersMap<F>) => void;
  resetFilters: () => void;
  setPagination: (page: number, pageSize?: number) => void;
  setSort: (sortBy: string, direction: SortingDirection) => void;
}

export interface TableQueryParamsResult<F extends string = string> extends TableQueryState<F>, TableQueryHandlers<F> {
  sortingColumnName?: string;
}

const applyFilterToParams = <F extends string>(
  params: URLSearchParams,
  definition: FilterDefinition<F>,
  value: GenericFiltersMap<F>[F] | undefined,
) => {
  switch (definition.type) {
    case "array": {
      const arrayValue = Array.isArray(value) ? value : undefined;
      const serializer = definition.serialize;
      const values = arrayValue
        ? arrayValue
            .map((entry) => {
              if (serializer) {
                return serializer(entry);
              }

              if (entry === undefined || entry === null) {
                return undefined;
              }

              return String(entry);
            })
            .filter((entry): entry is string => entry !== undefined && entry !== null && entry !== "")
        : undefined;

      setArrayParam(params, definition.param ?? definition.key, values);
      break;
    }
    case "range": {
      const range = value as RangeFilterValues | undefined;
      const effective = range ? createRangeValue(range.from, range.to) : undefined;
      setParam(params, definition.fromParam, effective?.from);
      setParam(params, definition.toParam, effective?.to);
      break;
    }
    case "value": {
      const serializer = definition.serialize;
      const serialized = serializer ? serializer(value) : value;
      setParam(params, definition.param ?? definition.key, serialized ?? undefined);
      break;
    }
    default:
      break;
  }
};

const parseArrayFilterFromParams = <F extends string, V>(
  params: URLSearchParams,
  definition: ArrayFilterDefinition<F, V>,
): V[] | undefined => {
  const rawValues = params.getAll(definition.param ?? definition.key).filter(Boolean);
  if (rawValues.length === 0) {
    return undefined;
  }

  const parser = definition.parse;
  const values = parser
    ? rawValues.map((raw) => parser(raw)).filter((parsed) => parsed !== undefined && parsed !== null)
    : rawValues;

  return values.length > 0 ? (values as V[]) : undefined;
};

const buildFiltersFromParams = <F extends string>(
  params: URLSearchParams,
  definitions: ReadonlyArray<FilterDefinition<F>>,
): GenericFiltersMap<F> => {
  const filters: GenericFiltersMap<F> = {};

  definitions.forEach((definition) => {
    switch (definition.type) {
      case "array": {
        const values = parseArrayFilterFromParams(params, definition);
        if (values) {
          filters[definition.key] = values;
        }
        break;
      }
      case "range": {
        const range = createRangeValue(params.get(definition.fromParam), params.get(definition.toParam));
        if (range) {
          filters[definition.key] = range;
        }
        break;
      }
      case "value": {
        const raw = params.get(definition.param ?? definition.key);
        const parser = definition.parse;
        const parsed = parser ? parser(raw) : raw ?? undefined;
        if (parsed !== undefined && parsed !== null && parsed !== "") {
          filters[definition.key] = parsed;
        }
        break;
      }
      default:
        break;
    }
  });

  return filters;
};

export const useTableQueryParams = <F extends string = string>(
  config: TableQueryParamsConfig<F>,
): TableQueryParamsResult<F> => {
  const { defaultPageSize = DEFAULT_PAGE_SIZE, filters: filterDefinitions = [], sorting = { valid: [] } } = config;

  const wasSearchChanged = useRef(false);
  const hasInitialized = useRef(false);

  const searchParam = DEFAULT_SEARCH_PARAM;
  const pageParam = DEFAULT_PAGE_PARAM;
  const pageSizeParam = DEFAULT_PAGE_SIZE_PARAM;
  const sortParam = DEFAULT_SORT_PARAM;
  const sortDirectionParam = DEFAULT_SORT_DIRECTION_PARAM;

  const validSortKeys = useMemo(() => new Set(sorting.valid ?? []), [sorting.valid]);
  const defaultSortBy = sorting.defaultSortBy;
  const defaultSortDirection = sorting.defaultDirection;
  const fallbackColumnName = sorting.fallbackColumnName;

  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsString = searchParams.toString();

  const state = useMemo((): TableQueryState<F> => {
    const params = new URLSearchParams(searchParamsString);
    const filters = buildFiltersFromParams(params, filterDefinitions);

    const searchValue = params.get(searchParam)?.trim() ?? "";
    const hasAnyParams = params.toString().length > 0;

    const rawSortBy = params.get(sortParam);
    const hasExplicitSort = rawSortBy ? validSortKeys.has(rawSortBy) : false;

    let sortBy: string | undefined = rawSortBy ?? undefined;

    if (!hasExplicitSort && defaultSortBy) {
      if ((!hasAnyParams && !hasInitialized.current) || (wasSearchChanged.current && !searchValue)) {
        sortBy = defaultSortBy;
      }
    }

    const rawSortDirection = params.get(sortDirectionParam) as SortingDirection | null;
    let sortDirection: SortingDirection | undefined;
    if (sortBy) {
      if (rawSortDirection && sortingDirections.has(rawSortDirection)) {
        sortDirection = rawSortDirection;
      } else if (
        hasExplicitSort ||
        (!hasAnyParams && !hasInitialized.current) ||
        (wasSearchChanged.current && !searchValue)
      ) {
        sortDirection = defaultSortDirection;
      }
    }

    const page = parsePositiveInteger(params.get(pageParam), DEFAULT_PAGE);
    const pageSize = parsePositiveInteger(params.get(pageSizeParam), defaultPageSize);

    wasSearchChanged.current = false;

    return {
      filters,
      search: searchValue,
      page,
      pageSize,
      sortBy,
      sortDirection,
    };
  }, [
    defaultPageSize,
    defaultSortBy,
    defaultSortDirection,
    filterDefinitions,
    pageParam,
    pageSizeParam,
    searchParam,
    searchParamsString,
    sortDirectionParam,
    sortParam,
    validSortKeys,
  ]);

  const updateSearchParams = useCallback(
    (mutator: (params: URLSearchParams) => void) => {
      const next = new URLSearchParams(searchParamsString);
      mutator(next);
      setSearchParams(next, { replace: true });
    },
    [searchParamsString, setSearchParams],
  );

  const setSearch = useCallback<TableQueryHandlers<F>["setSearch"]>(
    (value) => {
      const trimmed = value.trim();
      updateSearchParams((params) => {
        setParam(params, searchParam, trimmed);
        params.delete(pageParam);
        if (trimmed) {
          // Clear sorting when search is active
          params.delete(sortParam);
          params.delete(sortDirectionParam);
        } else if (defaultSortBy && defaultSortDirection) {
          // Restore default sorting when search is cleared
          setParam(params, sortParam, defaultSortBy);
          setParam(params, sortDirectionParam, defaultSortDirection);
        }
      });
      wasSearchChanged.current = true;
    },
    [defaultSortBy, defaultSortDirection, pageParam, searchParam, sortDirectionParam, sortParam, updateSearchParams],
  );

  const setFilters = useCallback<TableQueryHandlers<F>["setFilters"]>(
    (nextFilters) => {
      updateSearchParams((params) => {
        filterDefinitions.forEach((definition) => {
          applyFilterToParams(params, definition, nextFilters[definition.key]);
        });

        params.delete(pageParam);
      });
    },
    [filterDefinitions, pageParam, updateSearchParams],
  );

  const resetFilters = useCallback(() => {
    updateSearchParams((params) => {
      filterDefinitions.forEach((definition) => {
        switch (definition.type) {
          case "array":
            params.delete(definition.param ?? definition.key);
            break;
          case "range":
            params.delete(definition.fromParam);
            params.delete(definition.toParam);
            break;
          case "value":
            params.delete(definition.param ?? definition.key);
            break;
          default:
            break;
        }
      });

      params.delete(pageParam);
    });
  }, [filterDefinitions, pageParam, updateSearchParams]);

  const setPagination = useCallback<TableQueryHandlers<F>["setPagination"]>(
    (page, pageSize) => {
      updateSearchParams((params) => {
        if (pageSize) {
          setParam(params, pageSizeParam, String(pageSize));
        }

        setParam(params, pageParam, String(Math.max(page, DEFAULT_PAGE)));
      });
    },
    [pageParam, pageSizeParam, updateSearchParams],
  );

  const setSort = useCallback<TableQueryHandlers<F>["setSort"]>(
    (sortBy, direction) => {
      if (!validSortKeys.has(sortBy) || !sortingDirections.has(direction)) {
        return;
      }

      updateSearchParams((params) => {
        setParam(params, sortParam, sortBy);
        setParam(params, sortDirectionParam, direction);
        params.delete(pageParam);
      });
    },
    [pageParam, sortDirectionParam, sortParam, updateSearchParams, validSortKeys],
  );

  const sortingColumnName = useMemo(() => {
    if (!state.sortBy) {
      return undefined;
    }

    if (!sorting.valid || sorting.valid.length === 0) {
      return undefined;
    }

    return (sorting.sortKeyToColumnName ?? {})[state.sortBy] ?? fallbackColumnName ?? state.sortBy;
  }, [fallbackColumnName, sorting.sortKeyToColumnName, sorting.valid, state.sortBy]);

  // Initialize URL with default sort on first load if URL is empty and defaults are configured
  useEffect(() => {
    if (hasInitialized.current) {
      return;
    }

    hasInitialized.current = true;
    const params = new URLSearchParams(searchParamsString);
    const hasAnyParams = params.toString().length > 0;

    if (!hasAnyParams && defaultSortBy && defaultSortDirection) {
      const next = new URLSearchParams();
      setParam(next, sortParam, defaultSortBy);
      setParam(next, sortDirectionParam, defaultSortDirection);
      setSearchParams(next, { replace: true });
    }
  }, [defaultSortBy, defaultSortDirection, searchParamsString, setSearchParams, sortDirectionParam, sortParam]);

  return useMemo<TableQueryParamsResult<F>>(() => {
    return {
      ...state,
      setSearch,
      setFilters,
      resetFilters,
      setPagination,
      setSort,
      sortingColumnName,
    };
  }, [resetFilters, setFilters, setPagination, setSearch, setSort, sortingColumnName, state]);
};

export default useTableQueryParams;
